import React, { useEffect, useMemo, useState } from 'react';

import {
  AppstoreOutlined,
  BankOutlined,
  NotificationOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import {
  App,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Tag,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  CreateRequirementInput,
  RequirementDetail,
  TouchLatamTask,
  UpdateRequirementInput,
} from 'models/Requirement';

import { RequirementService } from 'services/RequirementService';

import appI18n from '../../../config/i18n';

import './CreateRequirementModal.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];
const DEFAULT_COUNTRY = 'CL';

interface Props {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  requirement?: RequirementDetail | null;
  defaultCountry?: string;
  handleModalCancel: () => void;
  onSaved: () => void;
}

interface FormValues {
  countryCode: string;
  taskId: number;
  referentialCapacity: number;
  name: string;
  leadAssociationDeadline: Dayjs;
  description?: string;
}

function CreateRequirementModal({
  isOpen,
  mode = 'create',
  requirement,
  defaultCountry,
  handleModalCancel,
  onSaved,
}: Props) {
  const isEditMode = mode === 'edit' && !!requirement;
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [tasks, setTasks] = useState<TouchLatamTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(false);
  const [tasksCountry, setTasksCountry] = useState<string>('');

  const countryCode = Form.useWatch('countryCode', form);
  const selectedTaskId = Form.useWatch('taskId', form);

  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId),
    [tasks, selectedTaskId]
  );

  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
    setTasks([]);
    setTasksCountry('');

    if (isEditMode && requirement) {
      form.setFieldsValue({
        countryCode: requirement.countryCode,
        taskId: requirement.taskId,
        referentialCapacity: requirement.referentialCapacity,
        name: requirement.name,
        leadAssociationDeadline: requirement.leadAssociationDeadline
          ? dayjs(requirement.leadAssociationDeadline)
          : undefined,
        description: requirement.description || undefined,
      });
      // Precarga inmediata de la campaña actual (para mostrar su label) y
      // carga del catálogo del país para poder elegir otra campaña activa.
      if (requirement.task) {
        setTasks([requirement.task]);
      }
      fetchTasks(requirement.countryCode);
    } else {
      form.setFieldsValue({
        countryCode: defaultCountry || DEFAULT_COUNTRY,
      });
    }
  }, [isOpen, isEditMode, requirement, defaultCountry, form]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) return; // país inmutable en edición
    if (!countryCode) return;
    if (countryCode === tasksCountry) return;
    // Al cambiar el país limpiamos la campaña seleccionada y recargamos.
    form.setFieldValue('taskId', undefined);
    fetchTasks(countryCode);
  }, [isOpen, isEditMode, countryCode]);

  const fetchTasks = async (country: string) => {
    setTasksLoading(true);
    const response = await RequirementService.getTasks(country);
    if (response.success) {
      setTasks(response.data as TouchLatamTask[]);
      setTasksCountry(country);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.scene.create.errors.fetch_tasks'),
        3.5
      );
    }
    setTasksLoading(false);
  };

  const countryOptions = useMemo(
    () =>
      COUNTRY_OPTIONS.map((opt) => {
        const code = opt.value.toLowerCase();
        return {
          value: opt.value,
          label: (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <img
                src={`https://flagcdn.com/16x12/${code}.png`}
                srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
                width={16}
                height={12}
                alt=""
                style={{ display: 'block' }}
              />
              <span>
                {t(
                  `scenes.requirements.scene.countries.${opt.value}`,
                  opt.value
                )}
              </span>
            </span>
          ),
        };
      }),
    [t]
  );

  const taskOptions = useMemo(() => {
    // Solo se ofrecen campañas activas.
    const visibleTasks = tasks.filter((task) => task.isActive);
    // En edición, si la campaña previamente elegida quedó inactiva, la
    // agregamos igual para que figure en el listado y no se rompa el valor.
    const currentTask = isEditMode ? requirement?.task : undefined;
    if (
      currentTask &&
      !visibleTasks.some((task) => task.id === currentTask.id)
    ) {
      visibleTasks.push(currentTask);
    }
    return visibleTasks.map((task) => ({
      value: task.id,
      label: task.name,
    }));
  }, [tasks, isEditMode, requirement]);

  const renderCampaignDetails = (task: TouchLatamTask) => {
    const category =
      i18n.language === 'en' ? task.categoryNameEn : task.categoryNameEs;

    const items: { icon: React.ReactNode; label: string; value: string }[] = [];
    if (task.companyName) {
      items.push({
        icon: <BankOutlined />,
        label: t('scenes.requirements.detail.campaign.company'),
        value: task.companyName,
      });
    }
    if (task.verticalName) {
      items.push({
        icon: <AppstoreOutlined />,
        label: t('scenes.requirements.detail.campaign.vertical'),
        value: task.verticalName,
      });
    }
    if (category) {
      items.push({
        icon: <TagsOutlined />,
        label: t('scenes.requirements.detail.campaign.category'),
        value: category,
      });
    }

    return (
      <div className="requirement-campaign-details">
        <div className="requirement-campaign-details-head">
          <span className="requirement-campaign-details-title">
            <NotificationOutlined style={{ marginRight: 6 }} />
            {t('scenes.requirements.scene.create.campaign_details')}
          </span>
          <Tag
            color={task.isActive ? 'green' : 'default'}
            style={{ marginRight: 0 }}
          >
            {task.isActive
              ? t('scenes.requirements.detail.campaign.active')
              : t('scenes.requirements.detail.campaign.inactive')}
          </Tag>
        </div>
        <div className="requirement-campaign-details-meta">
          {items.map((item) => (
            <span
              key={item.label}
              className="requirement-campaign-details-item"
            >
              <span className="requirement-campaign-details-icon">
                {item.icon}
              </span>
              <span className="requirement-campaign-details-label">
                {item.label}
              </span>
              <strong>{item.value}</strong>
            </span>
          ))}
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    const tz = dayjs.tz.guess();
    const leadAssociationDeadline = dayjs
      .tz(`${values.leadAssociationDeadline.format('YYYY-MM-DD')} 23:59:59`, tz)
      .utc()
      .toISOString();

    const description = values.description?.trim();

    if (isEditMode && requirement) {
      const body: UpdateRequirementInput = {
        name: values.name.trim(),
        referentialCapacity: values.referentialCapacity,
        description: description ? description : null,
        leadAssociationDeadline,
        taskId: values.taskId,
      };

      setSubmitting(true);
      const response = await RequirementService.updateRequirement(
        requirement.id,
        body
      );
      setSubmitting(false);

      if (response.success) {
        message.success(t('scenes.requirements.scene.edit.success'), 3);
        handleModalCancel();
        onSaved();
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t('scenes.requirements.scene.edit.errors.update_failed'),
          3.5
        );
      }
      return;
    }

    // Modo CREATE
    const body: CreateRequirementInput = {
      countryCode: values.countryCode,
      name: values.name.trim(),
      referentialCapacity: values.referentialCapacity,
      leadAssociationDeadline,
      taskId: values.taskId,
    };
    if (description) body.description = description;

    setSubmitting(true);
    const response = await RequirementService.createRequirement(body);
    setSubmitting(false);

    if (response.success) {
      handleModalCancel();
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.scene.create.errors.create_failed'),
        3.5
      );
    }
  };

  const modalTitle = isEditMode
    ? t('scenes.requirements.scene.edit.title')
    : t('scenes.requirements.scene.create.title');
  const modalOkText = isEditMode
    ? t('scenes.requirements.scene.edit.buttons.save')
    : t('scenes.requirements.scene.create.buttons.create');

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={handleModalCancel}
      onOk={handleSubmit}
      okText={modalOkText}
      cancelText={t('scenes.requirements.scene.create.buttons.cancel')}
      confirmLoading={submitting}
      destroyOnClose
      width={640}
      maskClosable={false}
      className="requirement-modal"
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />
      <Form form={form} layout="vertical" preserve={false}>
        <Row gutter={16}>
          {/* 1. País (fila completa) */}
          <Col xs={24}>
            <Form.Item
              name="countryCode"
              label={t('scenes.requirements.scene.create.labels.country')}
              rules={[{ required: true }]}
              tooltip={
                isEditMode
                  ? t('scenes.requirements.scene.edit.country_locked_tooltip')
                  : undefined
              }
            >
              <Select options={countryOptions} disabled={isEditMode} />
            </Form.Item>
          </Col>

          {/* 2. Nombre */}
          <Col xs={24}>
            <Form.Item
              name="name"
              label={t('scenes.requirements.scene.create.labels.name')}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.requirements.scene.create.errors.name_required'
                  ),
                },
                { max: 255 },
              ]}
            >
              <Input
                maxLength={255}
                placeholder={t(
                  'scenes.requirements.scene.create.placeholders.name'
                )}
              />
            </Form.Item>
          </Col>

          {/* 3. Campaña */}
          <Col xs={24}>
            <Form.Item
              name="taskId"
              label={t('scenes.requirements.scene.create.labels.task')}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.requirements.scene.create.errors.task_required'
                  ),
                },
              ]}
              style={selectedTask ? { marginBottom: 8 } : undefined}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={tasksLoading}
                disabled={tasksLoading}
                options={taskOptions}
                placeholder={t(
                  'scenes.requirements.scene.create.placeholders.task'
                )}
              />
            </Form.Item>
          </Col>

          {/* Detalle de la campaña seleccionada */}
          {selectedTask && (
            <Col xs={24}>{renderCampaignDetails(selectedTask)}</Col>
          )}

          {/* 4. Fecha límite + Capacidad referencial */}
          <Col xs={24} md={12}>
            <Form.Item
              name="leadAssociationDeadline"
              label={t('scenes.requirements.scene.create.labels.deadline')}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.requirements.scene.create.errors.deadline_required'
                  ),
                },
              ]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="referentialCapacity"
              label={t(
                'scenes.requirements.scene.create.labels.referential_capacity'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.requirements.scene.create.errors.referential_capacity_required'
                  ),
                },
                {
                  type: 'integer',
                  min: 1,
                  message: t(
                    'scenes.requirements.scene.create.errors.referential_capacity_invalid'
                  ),
                },
              ]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          {/* 5. Descripción */}
          <Col xs={24}>
            <Form.Item
              name="description"
              label={t('scenes.requirements.scene.create.labels.description')}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                rows={3}
                placeholder={t(
                  'scenes.requirements.scene.create.placeholders.description'
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default CreateRequirementModal;
